import React, { useState, useCallback } from "react";
import type { TabbedFeatureBlock } from "@/types/content";
import { responsiveCmsImage } from "@/utils/cmsImage";
import styles from "./TabbedFeature.module.css";

/** --tabbed-image-size per breakpoint; full width up to 500px on a phone. */
const TAB_IMAGE_SIZES =
  "(max-width: 500px) 100vw, (max-width: 768px) 500px, (max-width: 1024px) 380px, (max-width: 1200px) 450px, 600px";

export interface TabbedFeatureProps {
  block: TabbedFeatureBlock;
  className?: string;
}

/**
 * Tabbed Feature Block - split layout with an image on the left that
 * changes based on which tab is active on the right. Each tab has an
 * orange left-border indicator when selected.
 */
export const TabbedFeature: React.FC<TabbedFeatureProps> = ({
  block,
  className,
}) => {
  const { tabs } = block;
  const [activeIndex, setActiveIndex] = useState(0);

  const handleTabClick = useCallback((index: number) => {
    setActiveIndex(index);
  }, []);

  if (!tabs || tabs.length === 0) {
    return null;
  }

  const containerClasses = [styles.section, className]
    .filter(Boolean)
    .join(" ");

  return (
    <section
      className={containerClasses}
      data-component="tabbed-feature"
      aria-label="Feature highlights"
    >
      <div className={styles.container}>
        <div className={styles.layout}>
          {/* Left: image that changes per active tab */}
          <div className={styles.imagePanel} aria-live="polite">
            {tabs.map((tab, index) => (
              <img
                key={tab.id || index}
                {...responsiveCmsImage(
                  tab.image.url,
                  TAB_IMAGE_SIZES,
                  tab.image.width
                )}
                alt={tab.heading}
                className={`${styles.tabImage} ${index === activeIndex ? styles.tabImageActive : ""}`}
                loading={index === 0 ? "eager" : "lazy"}
              />
            ))}
          </div>

          {/* Right: tab buttons */}
          <div
            className={styles.tabsMenu}
            role="tablist"
            aria-label="Feature tabs"
          >
            {tabs.map((tab, index) => (
              <button
                key={tab.id || index}
                className={`${styles.tab} ${index === activeIndex ? styles.tabActive : ""}`}
                onClick={() => handleTabClick(index)}
                role="tab"
                aria-selected={index === activeIndex}
                aria-controls={`tabbed-feature-panel-${index}`}
                id={`tabbed-feature-tab-${index}`}
              >
                <h3 className={styles.tabHeading}>{tab.heading}</h3>
                <p className={styles.tabText}>{tab.text}</p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TabbedFeature;
